import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import api from "../../../../store/api";
import { AppThunk } from "../../../../store/app";
import Config from "../../../../config/Config";
import { apiUrls } from "./apiUrls";
import { skillAssessmentAnswerOptions } from "./constant";

interface InitialState {
  isLoading: boolean;
  agGrid: any;
  rowData: any;
  fileRowdata: any;
  perPage: any;
  details: any;
}

const initialState = {
  isLoading: false,
  agGrid: null,
  rowData: null,
  fileRowdata: null,
  perPage: Config.grid.server.gridOptions?.paginationPageSize,
  details: null,
} as InitialState;

const skillPortraitSlice = createSlice({
  name: "skillPortraitSlice",
  initialState,
  reducers: {
    start: (state) => {
      state.isLoading = true;
    },
    success: (state) => {
      state.isLoading = false;
    },
    failure: (state) => {
      state.isLoading = false;
    },
    setGrid: (state, action: PayloadAction<any>) => {
      state.agGrid = action?.payload;
    },
    setFileRowData: (state, action: PayloadAction<any>) => {
      state.fileRowdata = action?.payload;
    },
    setRowData: (state, action: PayloadAction<any>) => {
      state.rowData = action?.payload;
    },
    setPerPage: (state, action: PayloadAction<any>) => {
      state.perPage = action?.payload;
      if (state.agGrid) {
        state.agGrid.api.paginationSetPageSize(Number(state.perPage));
        state.agGrid.api.setCacheBlockSize(state.perPage);
      }
    },
    setDetails: (state, action: PayloadAction<any>) => {
      state.details = action?.payload;
    },
  },
});

export const {
  start,
  success,
  failure,
  setRowData,
  setGrid,
  setFileRowData,
  setPerPage,
  setDetails,
} = skillPortraitSlice.actions;

/** For Listing:Start */
export const fetchList =
  (action?: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(apiUrls.list, action);
        const modifiedData = response.data.rows.skill_potrait.map((row: any) => {
          var skill_categorys = []
          var careers = []
          var ocupationGroup = []
          if (row.skill_assessment_multi.length > 0) {
            skill_categorys = row.skill_assessment_multi.filter((item: any) => {
              return item.type == 1
            }).map((skill: any) => {
              return response.data.rows.skill_category.find((sk: any) => sk.id == skill.parent_id)
            })
            careers = row.skill_assessment_multi.filter((item: any) => {
              return item.type == 2
            }).map((career: any) => {
              return response.data.rows.careers.find((cr: any) => cr.id == career.parent_id)
            })
            ocupationGroup = row.skill_assessment_multi.filter((item: any) => {
              return item.type == 3
            }).map((career: any) => {
              return response.data.rows.occupational_group.find((og: any) => og.id == career.parent_id)
            })
          }

          return {
            ...row,
            skill_categorys,
            careers,
            ocupationGroup,
            answer_options: skillAssessmentAnswerOptions.find(
              (answer) => answer.id === row.answer_options
            )?.name,
          };
        });
        dispatch(setRowData(modifiedData));
        dispatch(success());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure());
        return Promise.reject(error.data);
      }
    };
/** For Listing:End */

export const setPerPageSize =
  (size: number): AppThunk<any> =>
    async (dispatch) => {
      dispatch(setPerPage(size));
      dispatch(fetchList());
    };

export const createRecord =
  (action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(apiUrls.create, action);
        dispatch(success(response.data));
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };

export const uplodeFile =
  (action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(apiUrls.uplodefile, action);
        dispatch(success(response.data));
        dispatch(setFileRowData(response.data.import_data));
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };

export const importFile =
  (action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(apiUrls.importfile, action);
        dispatch(success(response.data));
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };

export const updateRecord =
  (id: number, action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(apiUrls.update(id), action);
        dispatch(success(response.data));
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };

export const deleteRecord =
  (id: number, action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.delete(apiUrls.delete(id), action);
        dispatch(success(response.data));
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };

export const details =
  (id: number): AppThunk<any> =>
    async (dispatch) => {
      try {
        const response = await api.get(apiUrls.details(id));
        dispatch(setDetails(response.data.data));
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };

export const changeStatus =
  (id: number, status: number): AppThunk<any> =>
    async (dispatch) => {
      try {
        const response = await api.post(apiUrls.changeStatus(id), { status });
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };

export const changeOrder =
  (payload: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        const response = await api.post(apiUrls.changeOrder, payload);
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };

const skillPortraitReducer = skillPortraitSlice.reducer;
export default skillPortraitReducer;
